import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { TooltipProps } from 'recharts';
import { LoadingSpinner } from 'c-components';
import { NetworkRequestState } from 'c-data-layer';
import { ReportChartWrapper, ReportControlsWrapper } from 'c-reports/Components';
import type { ChartReportPart, ReportDateTimeframe, ReportType } from 'c-reports/Types';
import ImpactsMapView from './ImpactsMapView.main';

const groupByFieldWidgetDataCache = new Map<string, any[]>();
const groupByFieldWidgetInflightCache = new Map<string, Promise<any[]>>();

export const getWidgetChartAnimationKey = (
    data: any[] | null | undefined,
    xAxisDataKey: string,
    parts: ChartReportPart[],
) => {
    const rows = Array.isArray(data) ? data : [];

    return `${rows.length}-${rows
        .map(row => [row?.[xAxisDataKey], ...parts.map(part => row?.[part.dataKey])].join(':'))
        .join('|')}`;
};

type WidgetFilterOptionLabels = {
    expected: string;
    delivered: string;
    both: string;
};

export type GroupByFieldWidgetReportProps = {
    widgetId: string;
    reportType: ReportType.Line | ReportType.Bar;
    namePrefix: string;
    startDate?: Date;
    endDate?: Date;
    timeframe?: ReportDateTimeframe;
    xAxisDataKey: string;
    parts: ChartReportPart[];
    filterOptionLabels?: WidgetFilterOptionLabels;
    exportData?: any[];
    exportDataTransform?: (rows: any[]) => any[];
    requestKey: string;
    renderTooltipLabel?: (val: TooltipProps<any, any>) => JSX.Element;
    fetchData: (start: Date, end: Date, timeframe: ReportDateTimeframe) => Promise<any[]>;
    defaultShowNumbers?: boolean;
    allowPercentToggle?: boolean;
    percentOfTotal?: boolean;
    percentOfTotalLabel?: string;
    percentLabelDecimals?: number;
};

export const GroupByFieldWidgetReport: React.FC<GroupByFieldWidgetReportProps> = ({
    widgetId,
    reportType,
    namePrefix,
    startDate,
    endDate,
    timeframe,
    xAxisDataKey,
    parts,
    filterOptionLabels,
    exportData,
    exportDataTransform,
    requestKey,
    renderTooltipLabel,
    fetchData,
    defaultShowNumbers = false,
    allowPercentToggle = false,
    percentOfTotal = false,
    percentOfTotalLabel,
    percentLabelDecimals,
}) => {
    const [widgetData, setWidgetData] = useState<any[]>([]);
    const [loadingState, setLoadingState] = useState(NetworkRequestState.Idle);
    const fetchDataRef = useRef(fetchData);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    useEffect(() => {
        let isCancelled = false;

        if (!startDate || !endDate || timeframe == null) {
            return undefined;
        }

        const cacheKey = [
            requestKey,
            startDate.toISOString(),
            endDate.toISOString(),
            timeframe,
        ].join('-');
        const cachedData = groupByFieldWidgetDataCache.get(cacheKey);
        if (cachedData != null) {
            setWidgetData(cachedData);
            setLoadingState(NetworkRequestState.Success);
            return undefined;
        }

        setLoadingState(NetworkRequestState.InProgress);
        const currentFetchData = fetchDataRef.current;
        const inflightRequest =
            groupByFieldWidgetInflightCache.get(cacheKey) ??
            currentFetchData(startDate, endDate, timeframe);

        if (!groupByFieldWidgetInflightCache.has(cacheKey)) {
            groupByFieldWidgetInflightCache.set(cacheKey, inflightRequest);
        }

        inflightRequest
            .then(rows => {
                groupByFieldWidgetInflightCache.delete(cacheKey);
                if (isCancelled) return null;
                groupByFieldWidgetDataCache.set(cacheKey, rows);
                setWidgetData(rows);
                setLoadingState(NetworkRequestState.Success);
                return null;
            })
            .catch(() => {
                groupByFieldWidgetInflightCache.delete(cacheKey);
                if (isCancelled) return null;
                setWidgetData([]);
                setLoadingState(NetworkRequestState.Error);
                return null;
            });

        return () => {
            isCancelled = true;
        };
    }, [endDate, requestKey, startDate, timeframe]);

    const actualExportData = useMemo(
        () => exportData ?? exportDataTransform?.(widgetData),
        [exportData, exportDataTransform, widgetData],
    );

    const chartProps = {
        xAxisDataKey,
        data: widgetData,
        parts,
        renderTooltipLabel,
        filterOptionLabels,
        exportData: actualExportData,
        allowPercentToggle,
        percentOfTotal,
        percentOfTotalLabel,
        percentLabelDecimals,
    };
    const chartAnimationKey = getWidgetChartAnimationKey(widgetData, xAxisDataKey, parts);

    return (
        <Box id={widgetId} minHeight={500} width="100%" position="relative">
            {loadingState === NetworkRequestState.InProgress && (
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    zIndex={1}
                    sx={{ transform: 'translate(-50%, -50%)' }}
                >
                    <LoadingSpinner />
                </Box>
            )}
            <Box height={500}>
                <ReportControlsWrapper
                    chartProps={chartProps}
                    type={reportType}
                    namePrefix={namePrefix}
                    startDate={startDate ?? new Date()}
                    endDate={endDate ?? new Date()}
                    defaultShowNumbers={defaultShowNumbers}
                >
                    <ReportChartWrapper
                        key={chartAnimationKey}
                        {...chartProps}
                        type={reportType}
                        loadingState={loadingState}
                        chartProps={{
                            margin: { top: 20, right: 30, left: 65, bottom: 20 },
                        }}
                    />
                </ReportControlsWrapper>
            </Box>
        </Box>
    );
};

export type StaticChartWidgetReportProps = {
    widgetId: string;
    reportType: ReportType.Line | ReportType.Bar;
    namePrefix: string;
    startDate?: Date;
    endDate?: Date;
    xAxisDataKey: string;
    parts: ChartReportPart[];
    data: any[];
    loadingState: NetworkRequestState;
    filterOptionLabels?: WidgetFilterOptionLabels;
    exportData?: any[];
    exportDataTransform?: (rows: any[]) => any[];
    renderTooltipLabel?: (val: TooltipProps<any, any>) => JSX.Element;
    defaultShowNumbers?: boolean;
    allowPercentToggle?: boolean;
    percentOfTotal?: boolean;
    percentOfTotalLabel?: string;
    percentLabelDecimals?: number;
};

export const StaticChartWidgetReport: React.FC<StaticChartWidgetReportProps> = ({
    widgetId,
    reportType,
    namePrefix,
    startDate,
    endDate,
    xAxisDataKey,
    parts,
    data,
    loadingState,
    filterOptionLabels,
    exportData,
    exportDataTransform,
    renderTooltipLabel,
    defaultShowNumbers = false,
    allowPercentToggle = false,
    percentOfTotal = false,
    percentOfTotalLabel,
    percentLabelDecimals,
}) => {
    const actualExportData = useMemo(
        () => exportData ?? exportDataTransform?.(data),
        [data, exportData, exportDataTransform],
    );
    const chartProps = {
        xAxisDataKey,
        data,
        parts,
        renderTooltipLabel,
        filterOptionLabels,
        exportData: actualExportData,
        allowPercentToggle,
        percentOfTotal,
        percentOfTotalLabel,
        percentLabelDecimals,
    };
    const chartAnimationKey = getWidgetChartAnimationKey(data, xAxisDataKey, parts);

    return (
        <Box id={widgetId} minHeight={500} width="100%" position="relative">
            {loadingState === NetworkRequestState.InProgress && (
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    zIndex={1}
                    sx={{ transform: 'translate(-50%, -50%)' }}
                >
                    <LoadingSpinner />
                </Box>
            )}
            <Box height={500}>
                <ReportControlsWrapper
                    chartProps={chartProps}
                    type={reportType}
                    namePrefix={namePrefix}
                    startDate={startDate ?? new Date()}
                    endDate={endDate ?? new Date()}
                    defaultShowNumbers={defaultShowNumbers}
                >
                    <ReportChartWrapper
                        key={chartAnimationKey}
                        {...chartProps}
                        type={reportType}
                        loadingState={loadingState}
                        chartProps={{
                            margin: { top: 20, right: 30, left: 65, bottom: 20 },
                        }}
                    />
                </ReportControlsWrapper>
            </Box>
        </Box>
    );
};

export type MapWidgetReportProps = {
    widgetId: string;
    campaignId: number;
    data: any;
    mapDataType: any;
    mapDisplayMode: any;
    loadingState: NetworkRequestState;
};

export const MapWidgetReport: React.FC<MapWidgetReportProps> = ({
    widgetId,
    campaignId,
    data,
    mapDataType,
    mapDisplayMode,
    loadingState,
}) => (
    <Box id={widgetId} minHeight={500} width="100%" position="relative">
        {loadingState === NetworkRequestState.InProgress && (
            <Box
                position="absolute"
                top="50%"
                left="50%"
                zIndex={1}
                sx={{ transform: 'translate(-50%, -50%)' }}
            >
                <LoadingSpinner />
            </Box>
        )}
        <ImpactsMapView
            id={campaignId}
            data={data}
            mapDataType={mapDataType}
            mapDisplayMode={mapDisplayMode}
        />
    </Box>
);
